import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import React from 'react'

import type { Page, Post } from '@/payload-types'
import { hrefFromPageDoc } from '@/utilities/hrefFromPageDoc'
import { LocaleLink } from '@/components/LocaleLink'

type CMSLinkType = {
  appearance?: 'inline' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'posts' | 'categories' | 'tags'
    value: Page | Post | { slug?: string | null } | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
}

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    url,
  } = props

  // 2026-07-05: referência pode ser página/post/categoria/tag. Categoria usa a base `/categoria/`
  // (não `/categories/`); post/tag usam o próprio segmento. Bases default (o menu do header usa as
  // configuradas via resolveHeaderNavigation; aqui — blocos/CTAs — usa o default, que casa com as rotas).
  const refValue =
    type === 'reference' && reference?.value != null && typeof reference?.value === 'object'
      ? reference.value
      : null
  let href: string | null | undefined = url
  if (refValue) {
    if (reference!.relationTo === 'pages') {
      href = hrefFromPageDoc(refValue as Page)
    } else {
      const slug = (refValue as { slug?: string | null }).slug
      if (slug) {
        const base = reference!.relationTo === 'categories' ? 'categoria' : reference!.relationTo
        href = `/${base}/${slug}`
      }
    }
  }

  if (!href) return null

  const size = appearance === 'link' ? 'clear' : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline') {
    return (
      <LocaleLink className={cn(className)} href={href || url || ''} {...newTabProps}>
        {label && label}
        {children && children}
      </LocaleLink>
    )
  }

  return (
    <Button asChild className={className} size={size} variant={appearance}>
      <LocaleLink className={cn(className)} href={href || url || ''} {...newTabProps}>
        {label && label}
        {children && children}
      </LocaleLink>
    </Button>
  )
}
